// growthbook.ts
import { GrowthBook } from "@growthbook/growthbook-react";
import { APP_ENV } from "../../client";
import { logEvent } from "../../client/analytics/trackPageView";
import { storage, STORAGE_KEYS } from "../../utils/storage";
import { randomUUID } from "expo-crypto";
import Bugsnag from "@bugsnag/expo";
import { Platform } from "react-native";

export async function getStableId(): Promise<string> {
  // ❶ Try user details first – you may already know the email
  const user = await storage
    .load({
      key: STORAGE_KEYS.USER_DETAILS,
    })
    .catch(() => null);

  if (user?.detail?.email) return user.detail.email.toLowerCase();

  // ❷ Otherwise fall back to anonymous ID in storage
  const existing = await storage
    .load({ key: STORAGE_KEYS.GROWTHBOOK_ANON_ID })
    .catch(() => null);

  if (existing) return existing;

  const id = randomUUID();
  await storage.save({ key: STORAGE_KEYS.GROWTHBOOK_ANON_ID, data: id });
  return id;
}

// ── 2. single GrowthBook instance ────────────────────
export const gb = new GrowthBook({
  apiHost: "https://cdn.growthbook.io",
  clientKey: "sdk-mYWtfoVgtKBpUsj",
  enableDevMode: true, // enables Visual-Editor overlay in web dev
  attributes: {}, // set later once we have the stable ID
  trackingCallback: (experiment, result) => {
    /* ONE call – handles FullStory + GA behind the scenes */
    logEvent({
      key: "Experiment Viewed",
      data: {
        experimentId: experiment.key,
        variationId: result.variationId, // or result.key if you prefer
      },
    });
  },
});

// reload GrowthBook to apply any changes made to the attributes
export const reloadGrowthBook = () => {
  try {
    getStableId().then((id) => {
      console.log("[GrowthBook] stable ID set:", id);
      gb.setAttributes({ id }).then(() => {
        gb.init();
        console.log("[GrowthBook] initialized", id);
      });
    });
  } catch (error) {
    console.error("[GrowthBook] Error reloading GrowthBook: ", error);
    // You can also notify Bugsnag here if you have it set up
    Bugsnag.notify(error);
  }
};

// This enabled us to nicely add dataSet props to components
// without risk of blowing up apps.
// e.g
//  <View
//   {...webProps({ dataSet: { id: `some-container` } })}
//  />
export function webProps<T extends object>(props: T): T | {} {
  return Platform.OS === "web" ? props : {};
}
